import { X, Ruler, Weight, Layers, Palette, Grid3X3, Calculator, Tag } from 'lucide-react';
import type { Product } from '../store/productData';
import { translateColor, translateTexture, translateProductDesc } from '../store/productData';
import { useLanguage } from '../store/LanguageContext';
import { DiscountBadge, resolveDiscount } from './DiscountBadge';

interface Props {
  product: Product | null;
  onClose: () => void;
  onUseInCalculator?: (productId: string) => void;
}

export default function ProductDetailModal({ product, onClose, onUseInCalculator }: Props) {
  const { t } = useLanguage();

  if (!product) return null;

  const translatedColor = translateColor(product.color, t);
  const translatedTexture = translateTexture(product.texture, t);
  const translatedDesc = translateProductDesc(product.id, t);

  const categoryLabels: Record<string, string> = {
    modern: 'Modern',
    klassiek: 'Klassiek',
    landelijk: 'Landelijk',
    industrieel: 'Industrieel',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-[slideUp_0.3s_ease-out]"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center transition-colors"
        >
          <X size={16} className="text-white" />
        </button>

        {/* Image */}
        <div className="relative h-36 sm:h-72 overflow-hidden rounded-t-2xl">
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={e => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="600" height="300" fill="%23f0ece6"><rect width="600" height="300"/><text x="300" y="160" text-anchor="middle" fill="%23c47a3a" font-size="24" font-family="sans-serif">Gevelsteenbedrijf</text></svg>';
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
            <span className="inline-block px-2 py-0.5 bg-[#c47a3a] text-white text-[10px] font-bold rounded uppercase tracking-wider mb-1">
              {categoryLabels[product.category] || product.category}
            </span>
            <h2 className="text-xl font-bold text-white">{product.name}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Price + color swatch */}
          <div className="flex items-center justify-between mb-3 sm:mb-5">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg border border-gray-200 shadow-inner"
                style={{ backgroundColor: product.colorHex }}
                title={translatedColor}
              />
              <div>
                <p className="text-sm font-semibold text-[#4a4a4a]">{translatedColor}</p>
                <p className="text-xs text-[#8a7e74]">{translatedTexture}</p>
              </div>
            </div>
            <div className="text-right">
              {(() => {
                const info = resolveDiscount(product);
                if (info) {
                  const newPrice = Math.round(product.pricePerM2 * (1 - info.pct / 100) * 100) / 100;
                  return (
                    <>
                      <p className="text-xs text-[#8a7e74] line-through">€{product.pricePerM2}</p>
                      <p className={`text-2xl font-bold ${info.isLiquidation ? 'text-amber-700' : 'text-rose-700'}`}>€{newPrice.toFixed(2)}</p>
                      <DiscountBadge product={product} size="sm" />
                    </>
                  );
                }
                return (
                  <>
                    <p className="text-2xl font-bold text-[#c47a3a]">€{product.pricePerM2}</p>
                    <p className="text-xs text-[#8a7e74]">per m² incl. BTW</p>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Description */}
          <p className="text-xs sm:text-sm text-[#4a4a4a] leading-relaxed mb-3 sm:mb-5">
            {translatedDesc || product.description}
          </p>

          {/* Specs grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-5">
            <div className="bg-[#faf7f3] rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3">
              <Ruler size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">Afmetingen</p>
                <p className="text-xs sm:text-sm font-medium text-[#1a1a1a]">{product.dimensions}</p>
              </div>
            </div>
            <div className="bg-[#faf7f3] rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3">
              <Weight size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">Gewicht</p>
                <p className="text-xs sm:text-sm font-medium text-[#1a1a1a]">{product.weight}</p>
              </div>
            </div>
            <div className="bg-[#faf7f3] rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3">
              <Palette size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">Kleur</p>
                <p className="text-xs sm:text-sm font-medium text-[#1a1a1a]">{translatedColor}</p>
              </div>
            </div>
            <div className="bg-[#faf7f3] rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3">
              <Layers size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">Textuur</p>
                <p className="text-xs sm:text-sm font-medium text-[#1a1a1a]">{translatedTexture}</p>
              </div>
            </div>
            <div className="bg-[#faf7f3] rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3">
              <Grid3X3 size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">Categorie</p>
                <p className="text-xs sm:text-sm font-medium text-[#1a1a1a]">{categoryLabels[product.category] || product.category}</p>
              </div>
            </div>
            <div className="bg-[#faf7f3] rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3">
              <Tag size={16} className="text-[#c47a3a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">Artikelcode</p>
                <p className="text-xs sm:text-sm font-medium text-[#1a1a1a]">{product.supplierCode}</p>
              </div>
            </div>
          </div>

          {/* Action */}
          {onUseInCalculator && (
            <button
              onClick={() => {
                onUseInCalculator(product.id);
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 py-3 bg-[#c47a3a] text-white text-sm font-semibold rounded-xl hover:bg-[#b06a2a] transition-colors"
            >
              <Calculator size={16} /> {t('v6.kiesSteen')}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
